import { flushSync } from "react-dom";
import { MessageSquare, RotateCcw, Send, X } from "lucide-react";
import { agentStepFromNodeEvent, buildInnLiveEventsUrl, extractMessageFromOutput, getAnimationOrigin, friendlyNetworkError, friendlyRequestError, getAvailableWindowHeight, MAX_MESSAGE_LENGTH, MESSAGE_COUNT_WARNING_AT, readableTextColor, WINDOW_EDGE_GAP, getChatPosition, normalizeAgentStep, parseAdditionalHeaders } from "../utils";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useReducer } from "react";
import { AgentStep, ChatMessageType } from "../../types/chatWidget";
import ChatMessage from "./chatMessage";
import { fetchSessionStatus, sendMessage } from "../../controllers";
import ChatMessagePlaceholder from "../../chatPlaceholder";
import AgentProgressSteps from "./chatMessage/AgentProgressSteps";
import VoiceAssistantPanel from "./voiceAssistant";
import { base64ToFloat32Array } from "./voiceAssistant/helpers/utils";
import { workletCode } from "./voiceAssistant/helpers/streamProcessor";
import { startExecution, cancelExecution } from "../../api/execution";
import { streamExecutionWithReconnect } from "../../sse/SSEClient";
import { executionReducer } from "../../execution/executionReducer";
import { initialExecutionSession } from "../../execution/executionState";

const INNLIVE_EVENTS_BASE = "/api/v1/innlive/session";

// The page can't see inside the shadow DOM, so its shortcuts can't tell that a visitor is typing
const stopKeyEvent = (event: React.SyntheticEvent) => event.stopPropagation();

function readViewport() {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    visualHeight: window.visualViewport?.height ?? window.innerHeight,
    visualTop: window.visualViewport?.offsetTop ?? 0,
  };
}

const MAX_SUGGESTIONS = 4;

const isTouchDevice = () => window.matchMedia?.("(pointer: coarse)").matches ?? false;

export default function ChatWindow({
  api_key,
  flowId,
  hostUrl,
  updateLastMessage,
  messages,
  output_type,
  input_type,
  output_component,
  bot_message_style,
  send_icon_style,
  user_message_style,
  chat_window_style,
  error_message_style,
  placeholder_sending,
  send_button_style,
  online = true,
  open,
  online_message = "We'll reply as soon as we can",
  offline_message = "We're offline now",
  window_title = "Chat",
  welcome_message = "Hi! How can we help you today?",
  suggested_questions,
  onStartNewConversation,
  brand_color,
  brand_logo_url,
  placeholder,
  input_style,
  input_container_style,
  addMessage,
  removeLastMessages,
  position,
  triggerRef,
  width = 450,
  height = 650,
  tweaks,
  sessionId,
  additional_headers,
  onClose,
  voice_enabled = false,
}: {
  api_key?: string;
  output_type: string,
  input_type: string,
  output_component?: string,
  bot_message_style?: React.CSSProperties;
  send_icon_style?: React.CSSProperties;
  user_message_style?: React.CSSProperties;
  chat_window_style?: React.CSSProperties;
  error_message_style?: React.CSSProperties;
  send_button_style?: React.CSSProperties;
  online?: boolean;
  open: boolean;
  online_message?: string;
  placeholder_sending?: string;
  offline_message?: string;
  window_title?: string;
  welcome_message?: string;
  suggested_questions?: string[];
  onStartNewConversation: () => void;
  brand_color?: string;
  brand_logo_url?: string;
  placeholder?: string;
  input_style?: React.CSSProperties;
  input_container_style?: React.CSSProperties;
  tweaks?: { [key: string]: any };
  flowId: string;
  hostUrl: string;
  updateLastMessage: Function;
  messages: ChatMessageType[];
  addMessage: Function;
  removeLastMessages: (count: number) => void;
  position?: string;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  width?: number;
  height?: number;
  sessionId: React.MutableRefObject<string>;
  additional_headers?: { [key: string]: string } | string;
  onClose?: () => void;
  voice_enabled?: boolean;
}) {
  const [value, setValue] = useState<string>("");
  const ref = useRef<HTMLDivElement>(null);
  const messagesContainer = useRef<HTMLDivElement>(null);
  const followNewMessages = useRef(true); // Off while a visitor has scrolled up to read
  const wasOpen = useRef(open);
  const [confirmingRestart, setConfirmingRestart] = useState(false);
  const [windowPosition, setWindowPosition] = useState({ left: "0", top: "0" });
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 480);
  const [viewportSize, setViewportSize] = useState(readViewport);
  const inputRef = useRef<HTMLTextAreaElement>(null); /* User input Ref */
  const addMessageRef = useRef(addMessage);
  const [executionSession, dispatchExecutionEvent] = useReducer(
    executionReducer,
    initialExecutionSession
  );
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentExecutionIdRef = useRef<string>("");
  
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subscribedSessionIdRef = useRef<string>("");

  const seenIncomingMessageIdsRef = useRef<Set<string>>(new Set());
  const voiceWsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const microphoneRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<AudioWorkletNode | null>(null);
  const audioQueueRef = useRef<AudioBuffer[]>([]);
  const isPlayingRef = useRef(false);
  const inputAudioReadyRef = useRef(false);
  const allowMicStreamingRef = useRef(false);
  const voiceSequenceRef = useRef(0);
  const voiceAssistantTextRef = useRef("");
  const voiceTurnIdRef = useRef<string | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const silenceNudgeCountRef = useRef(0);
  const silenceNudgeActiveRef = useRef(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [voiceStatus, setVoiceStatus] = useState("");
  const [voiceConnecting, setVoiceConnecting] = useState(false);
  const [voiceModeAvailable, setVoiceModeAvailable] = useState(true);

  // Live-agent handoff state
  const [liveAgent, setLiveAgent] = useState<{ name: string } | null>(null);
  // Persist the agent name across events using a ref to avoid stale closures
  const liveAgentNameRef = useRef<string | null>(null);

  // Agent progress steps state — keyed by run_id, cleared when message arrives
  const [agentSteps, setAgentSteps] = useState<AgentStep[]>([]);
  const agentStepsRef = useRef<AgentStep[]>([]);
  // Tracks whether a message.completed event already added the bot reply for the
  // current execution. Prevents execution.completed from adding a duplicate.
  const messageReceivedRef = useRef(false);
  const streamingRef = useRef<{ id?: string; text: string } | null>(null);
  const lastAddedMessageIdRef = useRef<string | undefined>(undefined);
  const [isStreaming, setIsStreaming] = useState(false);

  function showStreamedChunk(chunk: string, id?: string) {
    const stream = streamingRef.current;
    if (stream && stream.id === id) {
      stream.text += chunk;
      updateLastMessage({ message: stream.text, isSend: false });
    } else {
      streamingRef.current = { id, text: chunk };
      // The backend announces a streamed message with its first chunk, then sends that chunk again
      if (id && lastAddedMessageIdRef.current === id) {
        updateLastMessage({ message: chunk, isSend: false });
      } else {
        addMessage({ message: chunk, isSend: false });
      }
    }
    setIsStreaming(true);
  }

  const voiceFeatureEnabled = Boolean(voice_enabled) && Boolean(voiceModeAvailable);
  const voiceSupported = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }

    const speechWindow = window as Window & {
      AudioContext?: typeof AudioContext;
    };

    return Boolean(
      speechWindow.AudioContext &&
        typeof navigator.mediaDevices?.getUserMedia === "function" &&
        window.AudioWorkletNode &&
        window.WebSocket,
    );
  }, []);
  const canUseVoice = voiceFeatureEnabled && voiceSupported;

  useEffect(() => {
    const normalizedHost = (hostUrl || "").trim().replace(/\/+$/, "");
    if (!normalizedHost) {
      return;
    }

    let cancelled = false;

    const loadVoiceModeAvailability = async () => {
      try {
        const headers: Record<string, string> = {};
        if (api_key) {
          headers["x-api-key"] = api_key;
        }
        if (additional_headers) {
          const extraHeaders = parseAdditionalHeaders(additional_headers) ?? {};
          Object.keys(extraHeaders).forEach((key) => {
            headers[key] = String(extraHeaders[key]);
          });
        }

        const response = await fetch(`${normalizedHost}/api/v1/config`, {
          headers,
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        if (!cancelled && typeof data?.voice_mode_available === "boolean") {
          setVoiceModeAvailable(data.voice_mode_available);
        }
      } catch (error) {
        console.warn("Failed to load public voice config", error);
      }
    };

    void loadVoiceModeAvailability();

    return () => {
      cancelled = true;
    };
  }, [additional_headers, api_key, hostUrl]);

  const sendVoiceEvent = useCallback(
    (type: string, payload: Record<string, unknown> = {}) => {
      const socket = voiceWsRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        return;
      }

      socket.send(
        JSON.stringify({
          type,
          protocol: "voice_protocol_v1",
          sequence: voiceSequenceRef.current++,
          ...payload,
        }),
      );
    },
    [],
  );

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const stopSilenceNudges = useCallback(() => {
    clearSilenceTimer();
    silenceNudgeCountRef.current = 0;
    silenceNudgeActiveRef.current = false;
  }, [clearSilenceTimer]);

  const fireSilenceNudge = useCallback(() => {
    if (
      !silenceNudgeActiveRef.current ||
      voiceWsRef.current?.readyState !== WebSocket.OPEN ||
      !isListening
    ) {
      return;
    }

    const nudges = [
      "Are you there? How can I help?",
      "I'm here whenever you're ready. What can I help you with?",
      "Still with you. Tell me what you need anytime.",
    ];
    const currentIndex = Math.min(silenceNudgeCountRef.current, nudges.length - 1);

    sendVoiceEvent("innossistai.silence_nudge", {
      message: nudges[currentIndex],
      count: silenceNudgeCountRef.current + 1,
      delay_ms: 22000,
    });

    silenceNudgeCountRef.current += 1;
    silenceTimerRef.current = setTimeout(fireSilenceNudge, 22000);
  }, [isListening, sendVoiceEvent]);

  const scheduleSilenceNudge = useCallback(
    (delayMs = 18000) => {
      clearSilenceTimer();
      silenceNudgeActiveRef.current = true;
      silenceTimerRef.current = setTimeout(fireSilenceNudge, delayMs);
    },
    [clearSilenceTimer, fireSilenceNudge],
  );

  // Helper: set live-agent state and keep ref in sync
  const setLiveAgentState = useCallback((name: string | null) => {
    liveAgentNameRef.current = name;
    setLiveAgent(name ? { name } : null);
  }, []);

  function upsertAgentStep(step: AgentStep) {
    setAgentSteps((prev) => {
      const idx = prev.findIndex((s) => s.step_id === step.step_id);
      const next =
        idx >= 0
          ? prev.map((s, i) => (i === idx ? { ...s, ...step } : s))
          : [...prev, step];
      agentStepsRef.current = next;
      return next;
    });
  }
  useEffect(() => {
    const updateViewport = () => {
      setIsMobile(window.innerWidth <= 480);
      setViewportSize(readViewport());
    };
    window.addEventListener('resize', updateViewport);
    // The on-screen keyboard only changes the visual viewport
    window.visualViewport?.addEventListener('resize', updateViewport);
    window.visualViewport?.addEventListener('scroll', updateViewport);
    return () => {
      window.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('scroll', updateViewport);
    };
  }, []);

  // A fixed size would push the header and close control off short or narrow screens
  const windowWidth = Math.min(width, viewportSize.width - 2 * WINDOW_EDGE_GAP);
  const windowHeight = Math.min(
    height,
    getAvailableWindowHeight(triggerRef.current?.getBoundingClientRect(), viewportSize.height, position),
  );

  useEffect(() => {
    if (isMobile) return; // On mobile we use CSS full-screen override
    if (triggerRef.current)
      setWindowPosition(
        getChatPosition(
          triggerRef.current.getBoundingClientRect(),
          windowWidth,
          windowHeight,
          position
        )
      );
  }, [triggerRef, windowWidth, windowHeight, position, isMobile]);

  /* Initial listener for loss of focus that refocuses User input after a small delay */

  const [sendingMessage, setSendingMessage] = useState(false);

  // Ensure additional_headers is always an object
  const parsedHeaders = useMemo(() => parseAdditionalHeaders(additional_headers), [additional_headers]);

  useEffect(() => {
    addMessageRef.current = addMessage;
  }, [addMessage]);

  const closeEventStream = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  // ── Initial session state fetch ──────────────────────────────────────────
  // On mount (or when session changes), check if the session is already in
  // human-handoff mode and restore the agent badge immediately (same as playground).
  useEffect(() => {
    const sid = sessionId.current;
    if (!sid || !hostUrl) return;

    fetchSessionStatus(hostUrl, sid, api_key, additional_headers)
      .then((res) => {
        const data = res.data;
        if (!data) return;
        // The public endpoint returns { mode: "ai" } for non-existent sessions
        if (data.mode === "human" || data.mode === "pending_handoff" || data.mode === "pending") {
          const agentName = data.handling_agent_name || "Support Agent";
          setLiveAgentState(agentName);
        } else {
          setLiveAgentState(null);
        }
      })
      .catch((err) => {
        // If 404, it means the session is new or not in handoff yet.
        // We stay in AI mode and let SSE handle future transitions.
        console.warn("[ChatWidget] Session status check failed (safe to ignore):", err.message);
        setLiveAgentState(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostUrl, setLiveAgentState, api_key]);

  const connectSessionEvents = useCallback(
    (nextSessionId?: string) => {
      const normalizedEventsBase = INNLIVE_EVENTS_BASE.trim();
      if (!normalizedEventsBase) {
        return;
      }
      const normalizedSessionId = (nextSessionId || "").trim();
      if (!normalizedSessionId) {
        return;
      }
      if (subscribedSessionIdRef.current === normalizedSessionId && eventSourceRef.current) {
        return;
      }

      closeEventStream();
      subscribedSessionIdRef.current = normalizedSessionId;

      const eventUrl = buildInnLiveEventsUrl(hostUrl, normalizedEventsBase, normalizedSessionId, api_key);
      if (!eventUrl) {
        return;
      }

      const source = new EventSource(eventUrl);
      eventSourceRef.current = source;

      // ── agent_message: messages from human agent ────────────────────────────
      source.addEventListener("agent_message", (event) => {
        try {
          const payload = JSON.parse((event as MessageEvent<string>).data || "{}") as {
            id?: string;
            text?: string;
            sender_name?: string;
            files?: string[];
            category?: string;
          };
          const incomingId = (payload.id || "").trim();
          if (incomingId) {
            if (seenIncomingMessageIdsRef.current.has(incomingId)) {
              return;
            }
            seenIncomingMessageIdsRef.current.add(incomingId);
          }
          const text = (payload.text || "").trim();
          if (!text) {
            return;
          }
          // Clear agent steps when real message arrives
          setAgentSteps([]);
          agentStepsRef.current = [];
          // Tag as human agent message so UI can render it differently
          addMessageRef.current({
            message: text,
            isSend: false,
            isAgent: true,
            agent_name: payload.sender_name || liveAgentNameRef.current || "Agent",
            files: payload.files,
            category: payload.category,
          });
        } catch (error) {
          console.error("Failed to parse agent_message event", error);
        }
      });

      // ── agent_step: AI reasoning steps ────────────────────────────────────
      source.addEventListener("agent_step", (event) => {
        try {
          const step = JSON.parse(
            (event as MessageEvent<string>).data || "{}",
          ) as AgentStep;
          if (step.step_id && step.run_id) {
            upsertAgentStep(step);
          }
        } catch (error) {
          console.error("Failed to parse agent_step event", error);
        }
      });

      // ── agent_joined: human agent took over ───────────────────────────────
      source.addEventListener("agent_joined", (event) => {
        try {
          const payload = JSON.parse((event as MessageEvent<string>).data || "{}") as {
            agent_name?: string;
          };
          const name = (payload.agent_name || "Support Agent").trim();
          setLiveAgentState(name);
        } catch (error) {
          console.error("Failed to parse agent_joined event", error);
        }
      });

      // ── ai_resumed / handoff_cancelled: AI took back control ──────────────
      const handleHandoffEnd = () => {
        setLiveAgentState(null);
      };
      source.addEventListener("ai_resumed", handleHandoffEnd);
      source.addEventListener("handoff_cancelled", handleHandoffEnd);

      // ── status_changed: session closed / archived ─────────────────────────
      source.addEventListener("status_changed", (event) => {
        try {
          const data = JSON.parse((event as MessageEvent<string>).data || "{}") as { status?: string };
          if (data.status === "closed" || data.status === "archived") {
            setLiveAgentState(null);
          }
        } catch (error) {
          console.error("Failed to parse status_changed event", error);
        }
      });

      source.onerror = () => {
        source.close();
        eventSourceRef.current = null;

        reconnectTimerRef.current = setTimeout(() => {
          connectSessionEvents(normalizedSessionId);
        }, 3000);
      };
    },
    [closeEventStream, hostUrl, setLiveAgentState, api_key],
  );

  useEffect(() => {
    connectSessionEvents(sessionId.current);
    return () => {
      closeEventStream();
      subscribedSessionIdRef.current = "";
    };
  }, [closeEventStream, connectSessionEvents, sessionId]);

  const sendChatMessage = useCallback(
    async (messageText: string) => {
      const trimmedMessage = messageText.trim();
      if (!trimmedMessage || sendingMessage) {
        return;
      }

      addMessage({ message: trimmedMessage, isSend: true });
      setSendingMessage(true);
      setValue("");
      followNewMessages.current = true;

      setAgentSteps([]);
      agentStepsRef.current = [];
      messageReceivedRef.current = false;
      currentExecutionIdRef.current = "";
      streamingRef.current = null;
      lastAddedMessageIdRef.current = undefined;
      setIsStreaming(false);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const initialResponse = await startExecution({
          signal: controller.signal,
          baseUrl: hostUrl,
          flowId,
          message: trimmedMessage,
          input_type,
          output_type,
          sessionId: sessionId.current,
          output_component,
          tweaks,
          api_key,
          additional_headers: parsedHeaders
        });

        if (!initialResponse.ok) {
           const errData = await initialResponse.json().catch(() => null);
           throw new Error(friendlyRequestError(initialResponse.status, errData?.detail));
        }

        // Backend doesn't generate executionId strictly yet before streaming headers,
        // Wait, the backend endpoint actually streams an execution.queued event with the ID!
        // We capture the ID in the onEvent handler and dispatch START_EXECUTION there, or just rely on events.
        
        await streamExecutionWithReconnect({
          baseUrl: hostUrl,
          executionId: "", // Will be extracted from execution.queued event in reducer?
          // Actually, our StreamExecutionWithReconnect takes executionId. We need it from the initial event!
          // Wait, the new startExecution endpoint returns the stream. The ID is in the first event.
          // Let's modify SSEClient slightly to update its executionId dynamically.
          // For now, let's just pass an empty string, we can't resume if it breaks BEFORE getting the ID, but that's very rare.
          initialResponse,
          signal: controller.signal,
          api_key,
          additional_headers: parsedHeaders,
          onConnected: () => {
             dispatchExecutionEvent({ type: "CONNECTION_CHANGED", payload: "connected" });
          },
          onDisconnect: () => {
             dispatchExecutionEvent({ type: "CONNECTION_CHANGED", payload: "reconnecting" });
          },
          onEvent: (sequence, event) => {
             if (controller.signal.aborted) return;
             // Handle state in reducer
             dispatchExecutionEvent({ type: "RECEIVE_EVENT", payload: { sequence, event } });

             // Handle UI updates directly for now (or let useEffect handle it, but for simplicity we keep it here)
             if (event.type === "execution.queued") {
                 // Hack to set executionId in the options object so reconnects work
                 // We will just dispatch and let the reducer handle it.
                 currentExecutionIdRef.current = event.data.execution_id;
                 dispatchExecutionEvent({ type: "START_EXECUTION", payload: { executionId: event.data.execution_id } });
             } else if (event.type === "message.delta" || event.type === "token") {
                if (event.data.chunk) {
                  showStreamedChunk(event.data.chunk, event.data.id);
                }
             } else if (event.type === "message.completed" || event.type === "add_message") {
                // The visitor's own message is already on screen
                const extracted = (event.data as { sender?: string }).sender === "User" ? "" : extractMessageFromOutput(event.data);
                if (extracted) {
                  messageReceivedRef.current = true;
                  // This message may be the one being streamed, and now holds its final text
                  const stream = streamingRef.current;
                  if (stream && event.data.id && stream.id === event.data.id) {
                    stream.text = extracted;
                    updateLastMessage({ message: extracted, isSend: false });
                  } else {
                    addMessage({ message: extracted, isSend: false });
                    lastAddedMessageIdRef.current = event.data.id;
                  }
                }
             } else if (event.type === "agent.step" || event.type === "agent_step") {
                 const step = normalizeAgentStep(event.data);
                 if (step) {
                    upsertAgentStep(step);
                 }
             } else if (event.type === "node.started") {
                 const step = agentStepFromNodeEvent(event.data, currentExecutionIdRef.current, "running");
                 if (step) {
                    upsertAgentStep(step);
                 }
             } else if (event.type === "node.completed") {
                 const step = agentStepFromNodeEvent(event.data, currentExecutionIdRef.current, "completed");
                 if (step) {
                    upsertAgentStep(step);
                 }
             } else if (event.type === "execution.completed") {
                 // Only add a message from the final result when message.completed
                 // hasn't already delivered it (e.g. Text Output nodes that don't
                 // emit an on_message event during execution).
                 if (!messageReceivedRef.current && !streamingRef.current) {
                   const extracted = extractMessageFromOutput(event.data);
                   if (extracted) {
                     addMessage({
                       message: extracted,
                       isSend: false,
                     });
                   }
                 }
                 setAgentSteps([]);
                 agentStepsRef.current = [];
             } else if (event.type === "execution.failed" || event.type === "error") {
                 addMessage({
                   message: event.data.error || "Execution failed",
                   isSend: false,
                   error: true,
                 });
             }
          }
        });

      } catch (err: any) {
        // Only starting a new chat aborts a run, and that conversation is already gone
        if (!controller.signal.aborted) {
          addMessage({
            message: friendlyNetworkError(err),
            isSend: false,
            error: true,
          });
          console.error(err);
        }
      } finally {
        if (abortControllerRef.current === controller) {
          setSendingMessage(false);
          dispatchExecutionEvent({ type: "CONNECTION_CHANGED", payload: "disconnected" });
        }
      }
    },
    [
      addMessage,
      api_key,
      flowId,
      hostUrl,
      input_type,
      output_component,
      output_type,
      parsedHeaders,
      sendingMessage,
      sessionId,
      tweaks,
      updateLastMessage,
    ],
  );

  const handleClick = () => {
    sendChatMessage(value);
    if (!isTouchDevice()) inputRef.current?.focus();
  };

  // The failed attempt is replaced, so the conversation doesn't show the question twice
  const lastIndex = messages.length - 1;
  const canRetry = !sendingMessage && Boolean(messages[lastIndex]?.error && messages[lastIndex - 1]?.isSend);
  const retryLastMessage = () => {
    const failedMessage = messages[lastIndex - 1].message;
    removeLastMessages(2);
    sendChatMessage(typeof failedMessage === "string" ? failedMessage : String(failedMessage));
  };

  const lastMessage = messages[lastIndex];
  const announcement = sendingMessage
    ? (isStreaming ? "" : "The assistant is typing")
    : (lastMessage && !lastMessage.isSend && typeof lastMessage.message === "string" ? lastMessage.message : "");

  const playNextAudioChunk = useCallback(() => {
    const audioBuffer = audioQueueRef.current.shift();
    if (!audioBuffer || !processorRef.current) {
      isPlayingRef.current = false;
      setIsSpeaking(false);
      return false;
    }

    isPlayingRef.current = true;
    setIsSpeaking(true);

    try {
      const playbackAudio = Float32Array.from(audioBuffer.getChannelData(0));
      processorRef.current.port.postMessage({
        type: "playback",
        audio: playbackAudio,
      });
      return true;
    } catch (error) {
      console.error("Error playing audio:", error);
      isPlayingRef.current = false;
      setIsSpeaking(false);
      return false;
    }
  }, []);

  const interruptPlayback = useCallback(() => {
    audioQueueRef.current.splice(0, audioQueueRef.current.length);
    isPlayingRef.current = false;
    setIsSpeaking(false);
    if (processorRef.current) {
      processorRef.current.port.postMessage({ type: "stop_playback" });
    }
  }, []);

  const stopVoiceSession = useCallback(() => {
    inputAudioReadyRef.current = false;
    allowMicStreamingRef.current = false;
    voiceSequenceRef.current = 0;
    voiceAssistantTextRef.current = "";
    voiceTurnIdRef.current = null;
    stopSilenceNudges();
    setVoiceConnecting(false);
    setIsListening(false);
    setVoiceStatus("");
    interruptPlayback();

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      mediaStreamRef.current = null;
    }

    if (microphoneRef.current) {
      microphoneRef.current.disconnect();
      microphoneRef.current = null;
    }

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }

    if (voiceWsRef.current) {
      try {
        voiceWsRef.current.close(1000);
      } catch {
        voiceWsRef.current.close();
      }
      voiceWsRef.current = null;
    }
  }, [interruptPlayback, stopSilenceNudges]);

  const handleVoiceWebSocketMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        if (data.turn_id) {
          voiceTurnIdRef.current = String(data.turn_id);
        }

        switch (data.type) {
          case "session.created":
          case "session.ready":
            return;

          case "turn.started":
            voiceAssistantTextRef.current = "";
            setVoiceStatus("Thinking...");
            return;

          case "turn.completed":
            if (voiceAssistantTextRef.current.trim()) {
              addMessage({
                message: voiceAssistantTextRef.current.trim(),
                isSend: false,
              });
              voiceAssistantTextRef.current = "";
            }
            setVoiceStatus("Listening...");
            setIsSpeaking(false);
            scheduleSilenceNudge(18000);
            return;

          case "turn.cancelled":
            voiceAssistantTextRef.current = "";
            setVoiceStatus("Listening...");
            setIsSpeaking(false);
            stopSilenceNudges();
            interruptPlayback();
            return;

          case "assistant.thinking":
            setVoiceStatus("Thinking...");
            return;

          case "assistant.text.start":
            voiceAssistantTextRef.current = "";
            stopSilenceNudges();
            return;

          case "assistant.text.delta":
            if (typeof data.delta === "string") {
              voiceAssistantTextRef.current += data.delta;
              setVoiceStatus("Preparing reply...");
            }
            return;

          case "assistant.text.done":
            if (voiceAssistantTextRef.current.trim()) {
              addMessage({
                message: voiceAssistantTextRef.current.trim(),
                isSend: false,
              });
              voiceAssistantTextRef.current = "";
            }
            return;

          case "assistant.audio.start":
            setVoiceStatus("Speaking...");
            setIsSpeaking(true);
            stopSilenceNudges();
            return;

          case "assistant.audio.delta":
          case "response.audio.delta":
            if (data.delta && audioContextRef.current) {
              const float32Data = base64ToFloat32Array(data.delta);
              const audioBuffer = audioContextRef.current.createBuffer(
                2,
                float32Data.length,
                24000,
              );
              audioBuffer.getChannelData(0).set(float32Data);
              audioBuffer.getChannelData(1).set(float32Data);
              audioQueueRef.current.push(audioBuffer);
              if (!isPlayingRef.current) {
                playNextAudioChunk();
              }
            }
            return;

          case "assistant.audio.done":
            if (!playNextAudioChunk()) {
              setIsSpeaking(false);
              setVoiceStatus("Listening...");
            }
            scheduleSilenceNudge(18000);
            return;

          case "assistant.interrupted":
          case "assistant.response.cancelled":
            interruptPlayback();
            setVoiceStatus("Listening...");
            stopSilenceNudges();
            return;

          case "user.speech.started":
            stopSilenceNudges();
            setVoiceStatus("Listening...");
            return;

          case "transcript.partial":
            if (typeof data.text === "string") {
              setVoiceStatus(`Heard: ${data.text}`);
            }
            stopSilenceNudges();
            return;

          case "transcript.final":
          case "conversation.item.input_audio_transcription.completed": {
            const transcript = String(data.text || data.transcript || "").trim();
            if (transcript) {
              addMessage({
                message: transcript,
                isSend: true,
              });
              setVoiceStatus(`Heard: ${transcript}`);
            }
            stopSilenceNudges();
            return;
          }

          case "response.content_part.added":
            if (data.part?.type === "text" && data.part.text) {
              voiceAssistantTextRef.current += String(data.part.text);
            }
            return;

          case "response.done":
            if (data.response?.status_details?.error?.code) {
              const errorCode = String(data.response.status_details.error.code).replaceAll("_", " ");
              setVoiceError(`API key error: ${errorCode}`);
              setVoiceStatus("");
            }
            return;

          case "error":
            setVoiceError(
              data.message ||
                data.error?.message ||
                data.detail ||
                "Voice assistant error",
            );
            setVoiceStatus("");
            stopVoiceSession();
            return;
        }

        if (data.type === "flow.build.progress") {
          const buildData = data.data;
          if (buildData?.event === "start") {
            setVoiceStatus("Thinking...");
            return;
          }

          if (buildData?.event === "end_vertex") {
            const text =
              buildData?.data?.build_data?.data?.results?.message?.text ?? "";
            if (text) {
              addMessage({
                message: text,
                isSend: false,
              });
            }
            setVoiceStatus("Speaking reply...");
            return;
          }

          if (buildData?.event === "end") {
            setVoiceStatus("Listening...");
            return;
          }
        }
      } catch (error) {
        console.error("Failed to parse voice websocket message", error);
      }
    },
    [
      addMessage,
      interruptPlayback,
      playNextAudioChunk,
      scheduleSilenceNudge,
      stopSilenceNudges,
      stopVoiceSession,
    ],
  );

  const startVoiceRecording = useCallback(async () => {
    try {
      if (!audioContextRef.current) {
        const AudioContextCtor =
          window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        audioContextRef.current = new AudioContextCtor({ sampleRate: 24000 });
      }

      if (audioContextRef.current.state === "suspended") {
        await audioContextRef.current.resume();
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
          sampleRate: 48000,
        },
      });

      mediaStreamRef.current = stream;
      microphoneRef.current = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
      microphoneRef.current.connect(analyserRef.current);

      const blob = new Blob([workletCode], { type: "application/javascript" });
      const workletUrl = URL.createObjectURL(blob);

      try {
        await audioContextRef.current.audioWorklet.addModule(workletUrl);
      } catch (error) {
        if (!(error instanceof DOMException && error.message.includes("already been loaded"))) {
          throw error;
        }
      } finally {
        URL.revokeObjectURL(workletUrl);
      }

      processorRef.current = new AudioWorkletNode(audioContextRef.current, "stream_processor");
      analyserRef.current.connect(processorRef.current);
      processorRef.current.connect(audioContextRef.current.destination);

      processorRef.current.port.onmessage = (messageEvent) => {
        if (
          messageEvent.data?.type === "input" &&
          messageEvent.data.audio &&
          voiceWsRef.current &&
          allowMicStreamingRef.current
        ) {
          const audioBytes = new Uint8Array(
            messageEvent.data.audio.buffer.slice(0),
          );
          let binaryAudio = "";
          for (let index = 0; index < audioBytes.length; index += 1) {
            binaryAudio += String.fromCharCode(audioBytes[index]);
          }
          const base64Audio = btoa(binaryAudio);

          sendVoiceEvent("input_audio_buffer.append", {
            audio: base64Audio,
            language: navigator.language || "en-US",
            turn_id: voiceTurnIdRef.current ?? undefined,
          });
        } else if (messageEvent.data?.type === "done") {
          if (allowMicStreamingRef.current) {
            sendVoiceEvent("input_audio_buffer.commit", {
              turn_id: voiceTurnIdRef.current ?? undefined,
            });
          }

          if (!playNextAudioChunk()) {
            isPlayingRef.current = false;
            setIsSpeaking(false);
            if (!allowMicStreamingRef.current) {
              allowMicStreamingRef.current = true;
              setVoiceStatus("Listening...");
            }
          }
        }
      };

      inputAudioReadyRef.current = true;
      setIsListening(true);
      setVoiceStatus(allowMicStreamingRef.current ? "Listening..." : "Connecting...");
      setVoiceError("");
    } catch (error) {
      console.error("Error starting voice recording:", error);
      setVoiceError(`Voice start failed: ${(error as Error).message}`);
      stopVoiceSession();
    }
  }, [playNextAudioChunk, sendVoiceEvent, stopVoiceSession]);

  const startVoiceConversation = useCallback(() => {
    if (!canUseVoice || voiceConnecting || isListening || sendingMessage) {
      return;
    }

    if (!sessionId.current) {
      setVoiceError("No session available for voice.");
      return;
    }

    const normalizedHost = (hostUrl || "").trim().replace(/\/+$/, "");
    if (!normalizedHost) {
      setVoiceError("Voice backend URL is missing.");
      return;
    }

    const wsBase = normalizedHost.replace(/^http/i, "ws");
    const apiKeyQuery = api_key ? `?x-api-key=${encodeURIComponent(api_key)}` : "";
    const wsUrl = `${wsBase}/api/v1/voice/ws/flow_tts/${flowId}/${sessionId.current}${apiKeyQuery}`;

    try {
      setVoiceConnecting(true);
      setVoiceError("");
      setVoiceStatus("Connecting...");
      allowMicStreamingRef.current = false;
      voiceSequenceRef.current = 1;
      voiceAssistantTextRef.current = "";
      voiceTurnIdRef.current = null;
      stopSilenceNudges();

      if (voiceWsRef.current?.readyState === WebSocket.OPEN) {
        voiceWsRef.current.close(1000);
      }

      const socket = new WebSocket(wsUrl);
      voiceWsRef.current = socket;

      socket.onopen = async () => {
        setVoiceConnecting(false);
        setVoiceStatus("Connecting...");

        sendVoiceEvent("session.start", {
          flow_id: flowId,
        });
        sendVoiceEvent("voice.settings", {
          voice: "echo",
          provider: "openai",
        });

        await startVoiceRecording();
        setVoiceStatus("Assistant greeting...");

        sendVoiceEvent("innossistai.greeting", {
          message:
            "greet the user warmly and briefly introduce yourself, then ask how you can help. Keep it under two sentences.",
        });
      };

      socket.onmessage = handleVoiceWebSocketMessage;
      socket.onclose = (event) => {
        if (event.code !== 1000) {
          console.warn(`Voice websocket closed with code ${event.code}`);
        }
        stopVoiceSession();
      };
      socket.onerror = (error) => {
        console.error("Voice websocket error:", error);
        setVoiceError("Voice connection error.");
        stopVoiceSession();
      };
    } catch (error) {
      console.error("Failed to start voice conversation:", error);
      setVoiceError("Failed to start voice conversation.");
      setVoiceConnecting(false);
    }
  }, [
    api_key,
    canUseVoice,
    flowId,
    handleVoiceWebSocketMessage,
    hostUrl,
    isListening,
    sendingMessage,
    sessionId,
    startVoiceRecording,
    stopVoiceSession,
    sendVoiceEvent,
    stopSilenceNudges,
    voiceConnecting,
  ]);

  const toggleVoiceSession = useCallback(() => {
    if (isListening || voiceConnecting) {
      stopVoiceSession();
      return;
    }

    startVoiceConversation();
  }, [isListening, startVoiceConversation, stopVoiceSession, voiceConnecting]);

  useEffect(() => {
    if (!open) {
      stopVoiceSession();
    }
  }, [open, stopVoiceSession]);

  useEffect(() => {
    return () => {
      stopVoiceSession();
    };
  }, [stopVoiceSession]);

  function restartChat() {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    stopVoiceSession();
    closeEventStream();
    onStartNewConversation();
    setLiveAgentState(null);
    setConfirmingRestart(false);
    setSendingMessage(false);
    setIsStreaming(false);
    setAgentSteps([]);
    agentStepsRef.current = [];
    streamingRef.current = null;
    setValue("");
    followNewMessages.current = true;
    connectSessionEvents(sessionId.current);
    if (!isTouchDevice()) inputRef.current?.focus();
  }

  const suggestions = Array.isArray(suggested_questions)
    ? suggested_questions.filter((question) => typeof question === "string" && question.trim()).slice(0, MAX_SUGGESTIONS)
    : [];

  // Scrolls only the message list. scrollIntoView could also scroll the page the chat is embedded in.
  useEffect(() => {
    const container = messagesContainer.current;
    if (!container) return;
    // The welcome screen starts at its top, which a short window couldn't show all at once
    if (messages.length === 0) container.scrollTop = 0;
    else if (followNewMessages.current) container.scrollTop = container.scrollHeight;
  }, [messages, agentSteps, sendingMessage, open, value]);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${input.scrollHeight}px`;
  }, [value, open, windowWidth, isMobile]);

  // Keyboard focus moves only when a visitor opens or closes the chat, or typing on the page would break
  useEffect(() => {
    if (open === wasOpen.current) return;
    wasOpen.current = open;
    if (!open) {
      setConfirmingRestart(false);
      const root = ref.current?.getRootNode() as ShadowRoot | undefined;
      if (root?.activeElement && ref.current?.contains(root.activeElement)) triggerRef.current?.focus();
      return;
    }
    followNewMessages.current = true;
    if (isTouchDevice()) return; // Would cover half the conversation with the keyboard
    const timer = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [open, triggerRef]);

  // A page can block the space key before the widget sees it, so type it here instead
  function insertBlockedSpace(input: HTMLTextAreaElement) {
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    // Applied now, because the next key can arrive before React would next render
    flushSync(() => setValue(input.value.slice(0, start) + " " + input.value.slice(end)));
    input.setSelectionRange(start + 1, start + 1);
  }

  const mobileWindowStyle: React.CSSProperties = isMobile
    ? { position: 'fixed', top: viewportSize.visualTop, left: 0, right: 0, width: '100%', height: viewportSize.visualHeight, zIndex: 99999, borderRadius: 0 }
    : { ...windowPosition, zIndex: 9999 };
  const headerTextColor = brand_color ? readableTextColor(brand_color) : undefined;
  // A light brand colour would barely show on the white message bar
  const sendIconColor = headerTextColor === "#ffffff" ? brand_color : headerTextColor;

  return (
    <div
      className={
        "cl-chat-window " +
        (isMobile ? "" : getAnimationOrigin(position)) +
        (open ? " cl-scale-100" : " cl-scale-0")
      }
      style={{ ...mobileWindowStyle, ...(brand_color ? { "--cl-brand": brand_color } as React.CSSProperties : {}) }}
      role="dialog"
      aria-label={window_title}
      onKeyDown={(event) => {
        stopKeyEvent(event);
        if (event.key !== "Escape") return;
        if (confirmingRestart) setConfirmingRestart(false);
        else if (onClose) onClose();
      }}
      onKeyUp={stopKeyEvent}
      onKeyPress={stopKeyEvent}
    >
      <div
        style={isMobile ? { ...chat_window_style, width: '100%', height: '100%', borderRadius: 0 } : { ...chat_window_style, width: windowWidth, height: windowHeight }}
        ref={ref}
        className="cl-window"
      >
        <div className="cl-header" style={{ backgroundColor: brand_color || undefined, color: headerTextColor }}>
          <div className="cl-header-row">
            <div className="cl-header-title">
              {brand_logo_url && <img src={brand_logo_url} alt="" className="cl-header-logo" />}
              <span>{window_title}</span>
            </div>
            <div className="cl-header-actions">
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={() => setConfirmingRestart(true)}
                  aria-label="Start a new chat"
                  title="Start a new chat"
                  className="cl-close"
                >
                  <RotateCcw aria-hidden="true" size={18} />
                </button>
              )}
              {onClose && (
                <button type="button" onClick={onClose} aria-label="Close chat" className="cl-close">
                  <X aria-hidden="true" size={20} />
                </button>
              )}
            </div>
          </div>
          <div className="cl-header-subtitle" style={{ color: headerTextColor, opacity: brand_color ? 0.85 : undefined }}>
            {liveAgent ? (
              <>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%',
                  backgroundColor: '#22c55e',
                  boxShadow: '0 0 0 2px rgba(34,197,94,0.35)',
                  flexShrink: 0,
                  display: 'inline-block',
                }} />
                {liveAgent.name} is here
              </>
            ) : online ? (
              <>
                <div className="cl-online-message"></div>
                {online_message}
              </>
            ) : (
              <>
                <div className="cl-offline-message"></div>
                {offline_message}
              </>
            )}
          </div>
        </div>
        {confirmingRestart && (
          <div className="cl-restart-confirm" role="group" aria-label="Start a new chat">
            <span>Clear this chat and start over?</span>
            <div className="cl-restart-confirm-actions">
              {/* Focus starts on Cancel, so a stray Enter can't clear the conversation */}
              <button type="button" className="cl-restart-cancel" autoFocus onClick={() => { setConfirmingRestart(false); inputRef.current?.focus(); }}>
                Cancel
              </button>
              <button type="button" className="cl-restart-yes" style={brand_color ? { color: headerTextColor } : undefined} onClick={restartChat}>
                New chat
              </button>
            </div>
          </div>
        )}
        <div
          className="cl-messages_container"
          ref={messagesContainer}
          tabIndex={0}
          aria-label="Conversation"
          onScroll={(event) => {
            const list = event.currentTarget;
            followNewMessages.current = list.scrollHeight - list.scrollTop - list.clientHeight < 80;
          }}
        >
          {messages.length === 0 && !sendingMessage && (
            <div className="cl-welcome">
              {brand_logo_url ? (
                <img src={brand_logo_url} alt="" className="cl-welcome-logo" />
              ) : (
                <div className="cl-welcome-icon" style={brand_color ? { color: headerTextColor } : undefined}>
                  <MessageSquare aria-hidden="true" size={26} />
                </div>
              )}
              <p className="cl-welcome-title">{welcome_message}</p>
              <p className="cl-welcome-hint">
                {suggestions.length > 0 ? "Pick a question or type your own below." : "Type your question below to get started."}
              </p>
              {suggestions.length > 0 && (
                <div className="cl-suggestions">
                  {suggestions.map((question) => (
                    <button type="button" key={question} className="cl-suggestion" onClick={() => sendChatMessage(question)}>
                      {question}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {messages.map((message, index) => (
            <ChatMessage
              bot_message_style={bot_message_style}
              user_message_style={brand_color ? { backgroundColor: brand_color, color: headerTextColor, ...user_message_style } : user_message_style}
              error_message_style={error_message_style}
              key={index}
              message={message.message}
              isSend={message.isSend}
              error={message.error}
              isAgent={message.isAgent}
              agent_name={message.agent_name}
              brand_color={brand_color}
              onRetry={canRetry && index === lastIndex ? retryLastMessage : undefined}
            />
          ))}
          {agentSteps.length > 0 && (
            <AgentProgressSteps steps={agentSteps} />
          )}
          {sendingMessage && !isStreaming && (
            <ChatMessagePlaceholder bot_message_style={bot_message_style} />
          )}
        </div>
        {/* Replies are read out once they finish, because a growing reply would be read over and over */}
        <div className="cl-sr-only" role="status" aria-live="polite">{announcement}</div>
        {value.length >= MESSAGE_COUNT_WARNING_AT && (
          <div className="cl-char-count">{value.length} / {MAX_MESSAGE_LENGTH}</div>
        )}
        <div style={input_container_style} className="cl-input_container">
          {/* Stays usable during a reply, so a visitor can write their next message */}
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                handleClick();
              }
              if (e.key === " " && e.defaultPrevented) insertBlockedSpace(e.currentTarget);
            }}
            rows={1}
            maxLength={MAX_MESSAGE_LENGTH}
            aria-label="Message"
            placeholder={
              liveAgent
                ? `Chatting with ${liveAgent.name}…`
                : sendingMessage
                  ? (placeholder_sending || "Thinking...")
                  : (placeholder || "Type your message...")
            }
            style={input_style}
            ref={inputRef}
            className="cl-input-element"
          />
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <VoiceAssistantPanel
              available={voiceFeatureEnabled}
              supported={voiceSupported}
              listening={isListening}
              speaking={isSpeaking}
              connecting={voiceConnecting}
              status={voiceStatus}
              error={voiceError}
              onToggle={toggleVoiceSession}
              onStop={stopVoiceSession}
            />
            <button
              type="button"
              style={send_button_style}
              className="cl-send-button"
              aria-label="Send message"
              disabled={sendingMessage || !value.trim()}
              onClick={handleClick}
            >
              <Send
                aria-hidden="true"
                style={brand_color ? { stroke: sendIconColor, ...send_icon_style } : send_icon_style}
                className={
                  "cl-send-icon " +
                  (!sendingMessage
                    ? "cl-notsending-message"
                    : "cl-sending-message")
                }
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
